import React, { useId, useState } from "react";
import { AlertCircle, Loader2, RotateCcw, UploadCloud } from "lucide-react";

import LogoCropDialog from "./LogoCropDialog";
import PageAvatar from "./PageAvatar";
import { cn } from "../utils/cn";
import type { UploadStatus } from "../pages/settings/components/DocumentUploadField";

const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,image/svg+xml";
const MAX_BYTES = 5 * 1024 * 1024;

type Props = {
  /** Business name — drives the initial shown before a logo exists. */
  name?: string;
  /** Local preview while uploading, or the stored URL once saved. */
  imageURL?: string;
  status: UploadStatus;
  error?: string;
  label?: string;
  hint?: string;
  /** Receives the cropped square PNG, ready to upload. */
  onSelect: (file: File) => void;
  onRetry: () => void;
  onTooLarge?: (message: string) => void;
};

/**
 * Circular logo picker shared by business onboarding (auth/BusinessProfile)
 * and the settings Business details tab. Reuses PageAvatar so a business with
 * no logo yet still gets its branded initial instead of an empty grey circle.
 */
const LogoUploadField = ({
  name,
  imageURL,
  status,
  error,
  label = "Business logo",
  hint = "PNG, JPG or SVG. Square works best, up to 5MB.",
  onSelect,
  onRetry,
  onTooLarge,
}: Props) => {
  const inputId = useId();
  const uploading = status === "uploading";

  // The file waiting to be cropped — nothing uploads until the crop is
  // confirmed, so cancelling leaves the current logo untouched.
  const [pending, setPending] = useState<File | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    // Reset first so picking the same file again still fires onChange.
    event.target.value = "";
    if (!selected) return;
    if (selected.size > MAX_BYTES) {
      onTooLarge?.("Logo must be 5MB or smaller");
      return;
    }
    // An SVG is already resolution-independent and usually drawn to fit its
    // own viewBox; putting it through a raster crop would only degrade it.
    if (selected.type === "image/svg+xml") {
      onSelect(selected);
      return;
    }
    setPending(selected);
  };

  return (
    <div className="flex w-full flex-col">
      <span className="text-[14px] font-medium text-foreground">{label}</span>

      <div className="mt-2 flex items-center gap-4">
        <div className="relative">
          <PageAvatar
            name={name}
            image={imageURL}
            className="size-[72px] border"
            textClassName="text-[24px]"
          />
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-foreground/50">
              <Loader2 className="size-5 animate-spin text-white" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <label
              htmlFor={inputId}
              className={cn(
                "inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-xl border border-input bg-muted/50 px-3 text-[13px] font-semibold text-foreground transition-colors hover:bg-muted",
                uploading && "pointer-events-none opacity-60"
              )}
            >
              <UploadCloud className="size-4" />
              {status === "uploaded" ? "Change logo" : "Upload logo"}
            </label>
            <input
              id={inputId}
              type="file"
              accept={ACCEPT}
              className="hidden"
              disabled={uploading}
              onChange={handleChange}
            />

            {status === "error" && (
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex h-9 items-center gap-1.5 rounded-xl px-2 text-[13px] font-semibold text-primary hover:underline"
              >
                <RotateCcw className="size-4" />
                Retry
              </button>
            )}
          </div>

          <p
            className={cn(
              "mt-1.5 flex items-center gap-1 text-[11px] font-medium",
              status === "error" ? "text-destructive" : "text-muted-foreground"
            )}
          >
            {status === "error" && <AlertCircle className="size-3 shrink-0" />}
            {status === "error" ? error || "Upload failed" : hint}
          </p>
        </div>
      </div>

      <LogoCropDialog
        file={pending}
        open={!!pending}
        onOpenChange={(next) => {
          if (!next) setPending(null);
        }}
        onCropped={onSelect}
      />
    </div>
  );
};

export default LogoUploadField;
